import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, ShoppingCart } from 'lucide-react';

/** One vector-search hit from an org data source (bl/vector_search.py shape). */
export interface SupplierSuggestion {
  suggestion_id: string;
  source_id: number;
  source_name: string;
  supplier_name?: string | null;
  part_number?: string | null;
  name: string;
  price?: number | null;
  currency?: string | null;
  /** Cosine distance — smaller is closer. */
  distance: number;
  raw_row?: Record<string, any>;
}

/** Show at most the TOP 2 hits per term (by similarity) — everything past
 *  that is hidden (Ben, 2026-08-24: long suggestion lists drowned the
 *  results; the tail is noise the user should not have to scan). */
export function trimSuggestionsForDisplay(hits: SupplierSuggestion[]): SupplierSuggestion[] {
  if (!Array.isArray(hits) || hits.length <= 2) return hits;
  return [...hits].sort((a, b) => (a.distance ?? 1) - (b.distance ?? 1)).slice(0, 2);
}

/**
 * The backend joins ALL embedded columns into `name` with " · "
 * (vector_search.py::_normalize_row) — e.g. "MG · AFS Parts · Non-Dangerous ·
 * SUMPASSY-ENGINEOIL". The last segment is the row's own description column
 * (most specific); the leading segments are shared brand/category cells.
 * Split them so the part name reads as the title and the rest as context.
 */
export const splitSuggestionName = (name: string): { primary: string; context: string[] } => {
  const segments = (name || '').split(' · ').map(s => s.trim()).filter(Boolean);
  if (segments.length < 2) return { primary: name, context: [] };
  return { primary: segments[segments.length - 1], context: segments.slice(0, -1) };
};

/**
 * One readable suggestion row, shared by the in-table strip
 * (MultiPartResultView) and the standalone block (DataSourceSuggestions).
 *
 * Layout is fixed two-zone — name on its own line(s), meta chips on a second
 * line, Add pinned right — because the previous single flex-wrap line broke
 * down with real-length names (price/supplier/badge scattered onto random
 * lines and rows blurred together).
 */
interface SupplierSuggestionRowProps {
  sugg: SupplierSuggestion;
  inCart: boolean;
  adding: boolean;
  onAdd?: () => void;
  /** Render the divider above this row (every row but the first). */
  withDivider?: boolean;
  /** Skip the per-row supplier text (the container already names the source). */
  hideSupplier?: boolean;
}

/** Similarity badge tint by match strength — strong / decent / weak. */
const similarityStyle = (pct: number): React.CSSProperties => {
  if (pct >= 80) return { color: '#137f67', backgroundColor: '#e6f5f0' };
  if (pct >= 60) return { color: '#6866ff', backgroundColor: '#eef0ff' };
  return { color: '#77726a', backgroundColor: '#f0ede9' };
};

const SupplierSuggestionRow: React.FC<SupplierSuggestionRowProps> = ({
  sugg, inCart, adding, onAdd, withDivider, hideSupplier,
}) => {
  const { t } = useTranslation();
  const { primary, context } = splitSuggestionName(sugg.name);
  const pct = Math.max(0, Math.round((1 - sugg.distance) * 100));
  return (
    <div
      className="transition-colors hover:bg-[#f3f0ea]"
      style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '8px 6px', margin: '0 -6px',
        borderRadius: '8px',
        borderTop: withDivider ? '1px solid #eee9e2' : 'none',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {context.length > 0 && (
          <div style={{ fontSize: '10px', color: '#9a958c', lineHeight: 1.3, marginBottom: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {context.join(' › ')}
          </div>
        )}
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#33312c', lineHeight: 1.35, overflowWrap: 'break-word' }}>
          {primary}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '3px' }}>
          {/* The lookup key (ID / SKU) designated in the source setup — the row's
              identity. Price is deliberately NOT shown here (raw price-list
              values mislead pre-cart); the cart line still carries it. */}
          {sugg.part_number && (
            <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: '10px', color: '#55524b', backgroundColor: '#efece6', borderRadius: '4px', padding: '1px 5px', whiteSpace: 'nowrap' }}>
              {sugg.part_number}
            </span>
          )}
          {sugg.supplier_name && !hideSupplier && (
            <span style={{ fontSize: '10px', color: '#9a958c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px' }}>
              {sugg.supplier_name}
            </span>
          )}
          <span
            title={t('chat.multiPart.similarityHint', { defaultValue: 'Semantic similarity to your request' })}
            style={{ fontSize: '9px', fontWeight: 600, borderRadius: '20px', padding: '1px 6px', whiteSpace: 'nowrap', ...similarityStyle(pct) }}
          >
            ~{pct}%
          </span>
        </div>
      </div>
      {onAdd && (
        <div style={{ flexShrink: 0 }}>
          {inCart ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 600, color: '#137f67', whiteSpace: 'nowrap' }}>
              <Check size={12} /> {t('chat.multiPart.inCart', { defaultValue: 'In cart' })}
            </span>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onAdd(); }}
              disabled={adding}
              className="transition-colors hover:bg-[#eef0ff]"
              title={t('chat.multiPart.addToCart', { defaultValue: 'Add to Cart' })}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '3px', fontSize: '10px', fontWeight: 600, color: '#6866ff', backgroundColor: 'transparent', border: '1px solid #6866ff', borderRadius: '6px', padding: '3px 8px', whiteSpace: 'nowrap', cursor: adding ? 'not-allowed' : 'pointer', opacity: adding ? 0.6 : 1 }}
            >
              {adding ? <Loader2 size={12} className="animate-spin" /> : <ShoppingCart size={12} />}
              {t('chat.multiPart.add', { defaultValue: 'Add' })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SupplierSuggestionRow;
